import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Globe, Linkedin } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { riseIn } from '../lib/scrollMotion';
import { scrollToSection, setPendingSection } from '../lib/sectionNavigation';

const socialLinks = [
  { name: 'Website', href: 'https://industrialdigitaltwin.org', icon: Globe },
  { name: 'LinkedIn', href: 'https://de.linkedin.com/company/industrial-digital-twin-association', icon: Linkedin },
];

const sectionLinks = [
  { name: 'Services', sectionId: 'services' },
  { name: 'How It Works', sectionId: 'how-it-works' },
  { name: 'Benefits', sectionId: 'benefits' },
  { name: 'Pricing', sectionId: 'pricing' },
  { name: 'FAQ', sectionId: 'faq' },
  { name: 'Contact', sectionId: 'contact' },
];

const companyLinks = [
  { name: 'About IDTA', href: 'https://industrialdigitaltwin.org' },
  { name: 'Privacy Policy', href: 'https://industrialdigitaltwin.org/en/privacy' },
  { name: 'Imprint', href: '/imprint' },
];

const KEYVISUAL = '/static/css/assets/img/ADX_Hub_Header_Keyvisual_Layer_transparent.png';

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleSectionClick = (sectionId) => {
    if (location.pathname !== '/') {
      setPendingSection(sectionId);
      navigate('/');
      return;
    }
    scrollToSection(sectionId);
  };

  return (
    <footer
      className="relative overflow-hidden bg-[#0028CD] px-4 py-9 text-white sm:px-6 md:px-8 md:py-14 lg:px-12 xl:px-16"
      style={{
        paddingLeft: 'max(1rem, env(safe-area-inset-left))',
        paddingRight: 'max(1rem, env(safe-area-inset-right))',
        paddingBottom: 'max(3rem, env(safe-area-inset-bottom))',
      }}
      data-testid="footer"
    >
      <img
        src={KEYVISUAL}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center opacity-[0.13]"
      />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_0%,rgba(255,255,255,0.12),transparent_28%),radial-gradient(circle_at_18%_100%,rgba(255,80,15,0.12),transparent_32%),linear-gradient(180deg,rgba(255,255,255,0.06),transparent_28%)]" />

      <motion.div
        {...riseIn(0, 60, 0.12)}
        className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 gap-8 text-left md:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] md:gap-12 xl:gap-20"
      >
        <div className="max-w-xl space-y-5 md:space-y-6">
          <a href="/" className="inline-block">
            <img
              src="/static/css/assets/img/IDTA_Logo_Grey_Web_M.png"
              alt="IDTA Logo"
              className="h-8 w-auto brightness-0 invert sm:h-10 lg:h-11"
            />
          </a>

          <div className="max-w-[21rem] text-sm leading-[1.7] text-white/84 sm:max-w-md sm:text-[0.95rem]">
            <p className="font-semibold text-white">Industrial Digital Twin Applications GmbH</p>
            <p>Lyoner Straße 18, 60528 Frankfurt am Main</p>
            <p className="mt-2">Telefon: +49 69 6603 1939</p>
            <p>
              E-Mail:{' '}
              <a href="mailto:contact@adx-hub.com" className="text-white transition-colors hover:text-white/75">
                contact@adx-hub.com
              </a>
            </p>
          </div>

          <a
            href="https://adx-hub.com/dashboard/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-11 w-full max-w-[15rem] items-center justify-center gap-2 rounded-xl border-2 border-white/75 bg-white/[0.06] px-7 text-sm font-semibold text-white transition-all hover:border-white hover:bg-white/[0.12] sm:w-auto"
          >
            Get Started
            <ArrowRight size={18} />
          </a>
        </div>

        <div className="grid grid-cols-1 gap-7 border-t border-white/14 pt-7 min-[420px]:grid-cols-2 min-[420px]:gap-8 sm:gap-12 md:border-t-0 md:pt-1 lg:gap-16 xl:gap-24">
          <div>
            <h3 className="mb-3 text-sm font-semibold text-white md:mb-5">Sections</h3>
            <ul className="space-y-2.5 text-sm text-white/76 md:space-y-3">
              {sectionLinks.map((item) => (
                <li key={item.name}>
                  <button
                    type="button"
                    onClick={() => handleSectionClick(item.sectionId)}
                    className="border-0 bg-transparent p-0 text-left text-inherit transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white/80"
                  >
                    {item.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold text-white md:mb-5">Company</h3>
            <ul className="space-y-2.5 text-sm text-white/76 md:space-y-3">
              {companyLinks.map((item) => (
                <li key={item.name}>
                  <a
                    href={item.href}
                    target={item.href.startsWith('http') ? '_blank' : undefined}
                    rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="transition-colors hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white/80"
                  >
                    {item.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </motion.div>

      <motion.div
        {...riseIn(0.12, 60, 0.1)}
        className="relative z-10 mx-auto mt-9 grid w-full max-w-7xl grid-cols-1 items-start gap-5 border-t border-white/18 pt-6 text-left text-sm text-white/68 sm:grid-cols-[1fr_auto] sm:items-center md:mt-12"
      >
        <p className="order-3 leading-relaxed sm:order-1">© 2026 Industrial Digital Twin Applications GmbH</p>
        <div className="order-1 flex flex-col items-start gap-3 sm:order-2 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
          <div className="flex gap-3">
            {socialLinks.map((item) => {
              const Icon = item.icon;
              return (
                <a
                  key={item.name}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex size-10 items-center justify-center rounded-full border border-white/28 bg-white/[0.06] text-white transition-colors hover:border-white/55 hover:bg-white/[0.12] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white/80"
                  aria-label={item.name}
                >
                  <Icon size={19} />
                </a>
              );
            })}
          </div>
          <p className="order-2 sm:order-none">All rights reserved.</p>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;
